import React, { useEffect, useState } from "react";
import { _validateField } from "../hooks/useForm/form-field-validation-functions";
import { Logger } from "../../../lib/logger/Logger";
import { Tooltip } from "@mui/material";
import { SupabaseTableEnum } from "../../../lib/supabase/supabaseTypes";
import { useCentralStore } from "../store/Central";
import { DataGrid } from "@mui/x-data-grid";
import { SmallDialog } from "../components/small-dialog.component";
import { CustomTextField } from "../forms/form-input-components/custom-input-components/custom-text-field.component";
import { showNotification } from "../store/Central/selectors";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import { supabase } from "@/lib/supabase";
import { RolesEnum } from "@/lib/types/roles.types";
import { useUserManagementStore } from "../store/UserManagement";
import { deDE } from "@mui/x-data-grid/locales";
import { sendTeamInviteEmail } from "@/lib/email";
import posthog from "posthog-js";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { PlusCircle, RefreshCw, Pencil, UserPlus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { TechnicianColorCell } from "../components/technician-color-cell/technician-color-cell.component";
import { UserUpdateForm } from "../forms/form-components/user/update/user-update.form";
import { EmptyProfileUpdateForm } from "../forms/form-components/empty-profile-update.form";
import { useDesktopsStore } from "../store/Desktops";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "../components/ui/dialog";

export const UserManagementPage: React.FC = () => {
	const columns = [
		{
			field: "email",
			headerName: "E-Mail",
			width: 300,
		},
		{
			field: "first_name",
			headerName: "Vorname",
			width: 150,
		},
		{
			field: "last_name",
			headerName: "Nachname",
			width: 150,
		},
		{
			field: "code",
			headerName: "Code",
			width: 150,
		},
		{
			field: "role",
			headerName: "Rolle",
			width: 200,
			valueGetter: (params: any) => {
				if (
					params.row?.email?.includes("support-") &&
					params.row?.email?.includes("@dentdesk.ch")
				) {
					return "Support";
				}
				return params.row.role;
			},
		},
		{
			field: "location",
			headerName: "Standort",
			width: 150,
			valueGetter: (params: any) => {
				const locId = params.row.location_id;
				if (!locId) return "Standard";
				const loc = locations.find((l: any) => l.id === locId);
				return loc ? loc.name : "Standard";
			},
		},
		{
			field: "color",
			headerName: "Farbe",
			width: 120,
			renderCell: (params: any) => {
				// Only show color picker for active users with IDs
				if (!params.row.id || params.row.deactivated_at) {
					return null;
				}
				return (
					<TechnicianColorCell
						userId={params.row.id}
						customColors={
							(organization?.profile_colors as Record<
								string,
								string
							>) || {}
						}
						onColorChange={handleColorChange}
					/>
				);
			},
		},
		{
			field: "actions",
			headerName: "Aktionen",
			width: 320,
			renderCell: (params: any) => {
				const userId = profile?.id;
				const isDeactivated = params.row.deactivated_at;
				const isSupport = params.row.role === "Support";
				const isOwner = params.row.role === "Besitzer";
				const isCurrentUser = params.row.id === userId;
				const isInvitePending =
					params.row.invited !== "-" && params.row.accepted === "-";

				if (isSupport || (isOwner && !isCurrentUser)) {
					return null;
				}

				const isEmptyProfile =
					params.row.role === "Mitarbeiter ohne Profil";
				const hasPendingConversionInvite =
					isEmptyProfile && Boolean(params.row.pending_invite_id);
				const showEditButton =
					!isInvitePending || hasPendingConversionInvite;

				return (
					<div className="flex flex-row gap-2">
						{showEditButton && (
							<Button
								variant="ghost"
								size="sm"
								onClick={() => handleEditUser(params.row)}
								aria-label="Benutzer bearbeiten"
								data-testid={`edit-user-${params.row.id}`}
							>
								<Pencil className="h-4 w-4" />
							</Button>
						)}
						{isEmptyProfile &&
							!isDeactivated &&
							!hasPendingConversionInvite && (
								<Tooltip
									title="Diesen Mitarbeiter zu einem Mitarbeiter mit Profil konvertieren. Eine Einladung wird per E-Mail verschickt; nach der Anmeldung werden alle bisherigen Zuweisungen automatisch übernommen."
									placement="top"
								>
									<Button
										variant="outline"
										size="sm"
										onClick={() =>
											handleCreateProfileForEmptyProfile(
												params.row
											)
										}
										aria-label="Profil erstellen"
										data-testid={`create-profile-${params.row.id}`}
									>
										<UserPlus className="h-4 w-4" />
									</Button>
								</Tooltip>
							)}
						<Tooltip
							title={
								hasPendingConversionInvite
									? "Die ausstehende Einladung zurückziehen. Der Mitarbeiter ohne Profil bleibt erhalten."
									: "Der Techniker kann sich nicht mehr anmelden und wird auch nicht mehr in der Auswahl angezeigt."
							}
							placement="right"
						>
							<Button
								variant={
									isDeactivated ? "default" : "destructive"
								}
								size="sm"
								onClick={() =>
									handleDeactivateUser(
										params.row,
										isDeactivated
									)
								}
							>
								{hasPendingConversionInvite
									? "Einladung zurückziehen"
									: isInvitePending
										? "Löschen"
										: isDeactivated
											? "Aktivieren"
											: "Deaktivieren"}
							</Button>
						</Tooltip>
					</div>
				);
			},
		},
		{
			field: "invited",
			headerName: "Eingeladen",
			width: 150,
		},
		{
			field: "accepted",
			headerName: "Einladung angenommen",
			width: 200,
		},
	];

	const navigate = useNavigate();
	const {
		organizationId,
		subscription,
		organization,
		updateProfileColors,
		profile,
	} = useCentralStore((state) => ({
		organizationId: state.organization?.id,
		subscription: state.subscription,
		organization: state.organization,
		updateProfileColors: state.updateProfileColors,
		role: state.role ?? 2,
		profile: state.profile,
	}));
	const { members, getMembers, activeUsers } = useUserManagementStore(
		(state) => ({
			members: Object.values(state.membersLookup),
			getMembers: state.getMembers,
			activeUsers: state.getActiveMembers(new Date()),
		})
	);
	const { locations, fetchLocations } = useDesktopsStore((state) => ({
		locations: state.locations,
		fetchLocations: state.fetchLocations,
	}));

	useEffect(() => {
		getMembers();
		if (organizationId) {
			fetchLocations(organizationId);
		}
	}, []);

	const [email, setEmail] = useState("");
	const [validationError, setValidationError] = useState<string | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [supportDialogOpen, setSupportDialogOpen] = useState(false);
	const [courierDialogOpen, setCourierDialogOpen] = useState(false);
	const [courierEmail, setCourierEmail] = useState("");
	const [courierFirstName, setCourierFirstName] = useState("");
	const [courierLastName, setCourierLastName] = useState("");
	const [courierValidationError, setCourierValidationError] = useState<
		string | null
	>(null);
	const [isProfile, setIsProfile] = useState(true);
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [code, setCode] = useState("");
	const [showOnlyActiveUsers, setShowOnlyActiveUsers] = useState(true);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState<any>(null);
	const [convertingFromEmptyProfileId, setConvertingFromEmptyProfileId] =
		useState<string | null>(null);
	const [convertingFromLabel, setConvertingFromLabel] = useState<string>("");

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		const name = "email";
		const error = _validateField(name, value);
		setEmail(value);
		setValidationError(error);
	};

	const closeDialog = () => {
		setDialogOpen(false);
		setEmail("");
		setValidationError(null);
		setConvertingFromEmptyProfileId(null);
		setConvertingFromLabel("");
	};

	const openDialog = () => {
		setDialogOpen(true);
	};

	const handleDeactivateUser = async (
		row: any,
		isCurrentlyDeactivated: boolean
	) => {
		const isJustInvited = row.invited !== "-" && row.accepted === "-";
		const isRealUser = row.role !== "Mitarbeiter ohne Profil";
		const hasPendingConversionInvite =
			!isRealUser && Boolean(row.pending_invite_id);
		Logger.log(row, {}, "row");
		Logger.log(isCurrentlyDeactivated, {}, "isCurrentlyDeactivated");
		Logger.log(isJustInvited, {}, "isJustInvited");
		Logger.log(isRealUser, {}, "isRealUser");

		if (hasPendingConversionInvite) {
			const { error } = await supabase
				.from(SupabaseTableEnum.ALLOWED_SIGNUPS_LAB)
				.delete()
				.eq("id", row.pending_invite_id);

			if (error) {
				showNotification({
					message:
						"Beim Zurückziehen der Einladung ist ein Fehler aufgetreten.",
					type: "error",
				});
				Logger.error(error, {}, "withdrawConversionInvite");
				return;
			}

			showNotification({
				message: "Einladung erfolgreich zurückgezogen.",
				type: "success",
			});
			await getMembers();
			return;
		}

		if (
			(!subscription ||
				(subscription?.quantity ?? 0) < activeUsers.length + 1) &&
			isCurrentlyDeactivated &&
			subscription?.status !== "canceled" &&
			subscription?.status !== "trialing"
		) {
			showNotification({
				message:
					"Ihr Abonnement ist nicht ausreichend für die Anzahl der Nutzer. Möchten Sie Ihr Abonnement anpassen?",
				type: "error",
				action: {
					label: "Abonnement anpassen",
					onClick: () => {
						navigate("/abonnement");
					},
				},
			});
			return;
		}

		if (isRealUser && !isJustInvited) {
			Logger.log("Deactivating real user", "deactivateUser");
			// Wenn der Benutzer ein Profil hat
			const { error } = await supabase
				.from(SupabaseTableEnum.PROFILES)
				.update({
					deactivated_at: !isCurrentlyDeactivated
						? new Date().toISOString()
						: null,
				})
				.eq("id", row.id);

			if (error) {
				showNotification({
					message:
						"Beim Deaktivieren des Nutzers ist ein Fehler aufgetreten.",
					type: "error",
				});
				Logger.error(error, {}, "deactivateUser");
				return;
			}
		} else if (isRealUser && isJustInvited) {
			// Wenn der Benutzer nur in allowed_signups_lab existiert
			const { data, error } = await supabase
				.from(SupabaseTableEnum.ALLOWED_SIGNUPS_LAB)
				.delete()
				.eq("id", row.id);

			Logger.log(data, {}, "data");
			if (error) {
				showNotification({
					message:
						"Beim Deaktivieren der Einladung ist ein Fehler aufgetreten.",
					type: "error",
				});
				Logger.error(error, {}, "deactivateInvitation");
				return;
			}
			showNotification({
				message: "Einladung erfolgreich gelöscht.",
				type: "success",
			});
			await getMembers();
			return;
		} else if (!isRealUser) {
			// Wenn der Benutzer kein Profil hat
			const { error } = await supabase
				.from(SupabaseTableEnum.EMPTY_PROFILES)
				.update({
					deactivated_at: !isCurrentlyDeactivated
						? new Date().toISOString()
						: null,
				})
				.eq("id", row.id);

			if (error) {
				showNotification({
					message:
						"Beim Deaktivieren des Nutzers ist ein Fehler aufgetreten.",
					type: "error",
				});
				Logger.error(error, {}, "deactivateUser");
				return;
			}
		}

		showNotification({
			message: isCurrentlyDeactivated
				? "Nutzer wurde aktiviert."
				: "Nutzer wurde deaktiviert.",
			type: "success",
		});
		await getMembers();
	};

	const submitInvite = async () => {
		if (validationError) {
			showNotification({
				message: "Bitte überprüfen Sie Ihre Eingabe.",
				type: "error",
			});
			return;
		}

		if (!organizationId) {
			showNotification({
				message:
					"Die Organisation konnte nicht gefunden werden. Bitte wenden Sie sich an den Support.",
				type: "error",
			});
			return;
		}

		if (isProfile) {
			let fromEmptyProfileId = convertingFromEmptyProfileId;
			if (!fromEmptyProfileId) {
				if (!code && !firstName && !lastName) {
					showNotification({
						message:
							"Bitte geben Sie mindestens einen Code, einen Vornamen oder einen Nachnamen ein.",
						type: "warning",
					});
					return;
				}
				const { data: emptyProfile, error: emptyProfileError } =
					await supabase
						.from(SupabaseTableEnum.EMPTY_PROFILES)
						.insert({
							code: code,
							first_name: firstName,
							last_name: lastName,
							organization_id: organizationId,
						})
						.select("id")
						.single();

				if (emptyProfileError || !emptyProfile) {
					showNotification({
						message:
							"Beim Erstellen des Mitarbeiters ist ein Fehler aufgetreten. Bitte wenden Sie sich an den Support.",
						type: "error",
					});
					Logger.error(emptyProfileError, {}, "submitInvite");
					return;
				}
				fromEmptyProfileId = emptyProfile.id;
			}

			const { error } = await supabase
				.from(SupabaseTableEnum.ALLOWED_SIGNUPS_LAB)
				.insert({
					email,
					role,
					organization_id: organizationId,
					from_empty_profile_id: fromEmptyProfileId,
				});

			if (error) {
				if (!convertingFromEmptyProfileId && fromEmptyProfileId) {
					await supabase
						.from(SupabaseTableEnum.EMPTY_PROFILES)
						.delete()
						.eq("id", fromEmptyProfileId);
				}
				showNotification({
					message:
						"Beim Erstellen des Mitarbeiters ist ein Fehler aufgetreten. Bitte wenden Sie sich an den Support.",
					type: "error",
				});
				Logger.error(error, {}, "submitInvite");
				return;
			}

			const organization = useCentralStore.getState().organization;
			sendTeamInviteEmail({
				recipientEmail: email,
				labName: organization?.name ?? "",
			});
			posthog.capture("team_invite_sent", {
				recipient_email: email,
				role: role === RolesEnum.ADMIN ? "ADMIN" : "MEMBER",
				source: "user_management",
			});
			posthog.group("organization", organizationId ?? "", {
				$set_once: { first_invite_sent_at: new Date().toISOString() },
			});
			showNotification({
				message: "Einladung erfolgreich versendet.",
				type: "success",
			});
		} else {
			if (!code && !firstName && !lastName) {
				showNotification({
					message:
						"Bitte geben Sie mindestens einen Code, einen Vornamen oder einen Nachnamen ein.",
					type: "warning",
				});
				return;
			}
			const { error } = await supabase
				.from(SupabaseTableEnum.EMPTY_PROFILES)
				.insert({
					code: code,
					first_name: firstName,
					last_name: lastName,
					organization_id: organizationId,
				});

			if (error) {
				showNotification({
					message:
						"Beim Erstellen des Mitarbeiters ohne Profil ist ein Fehler aufgetreten. Bitte wenden Sie sich an den Support.",
					type: "error",
				});
				Logger.error(error, {}, "submitInvite");
				return;
			}

			showNotification({
				message: "Mitarbeiter erfolgreich erstellt.",
				type: "success",
			});
		}

		getMembers();
		closeDialog();
		setIsProfile(true);
		setRole(RolesEnum.MEMBER);
		setEmail("");
		setFirstName("");
		setLastName("");
		setCode("");
	};

	const submitCourierInvite = async () => {
		const error = _validateField("email", courierEmail);
		if (error) {
			setCourierValidationError(error);
			return;
		}

		if (!organizationId) {
			showNotification({
				message: "Organisation nicht gefunden.",
				type: "error",
			});
			return;
		}

		if (!courierFirstName.trim() || !courierLastName.trim()) {
			showNotification({
				message: "Bitte Vor- und Nachnamen eingeben.",
				type: "warning",
			});
			return;
		}

		const { error: insertError } = await supabase
			.from(SupabaseTableEnum.ALLOWED_SIGNUPS_LAB)
			.insert({
				email: courierEmail,
				role: RolesEnum.COURIER,
				organization_id: organizationId,
			});

		if (insertError) {
			showNotification({
				message: "Fehler beim Einladen des Kuriers.",
				type: "error",
			});
			Logger.error(insertError, {}, "submitCourierInvite");
			return;
		}

		const organization = useCentralStore.getState().organization;
		sendTeamInviteEmail({
			recipientEmail: courierEmail,
			labName: organization?.name ?? "",
		});
		posthog.capture("team_invite_sent", {
			recipient_email: courierEmail,
			role: "COURIER",
			source: "user_management",
		});
		posthog.group("organization", organizationId ?? "", {
			$set_once: { first_invite_sent_at: new Date().toISOString() },
		});

		showNotification({
			message: "Kurier-Einladung versendet.",
			type: "success",
		});
		getMembers();
		setCourierDialogOpen(false);
		setCourierEmail("");
		setCourierFirstName("");
		setCourierLastName("");
		setCourierValidationError(null);
	};

	const handleSupportAccountCreation = async () => {
		if (!organizationId) {
			showNotification({
				message:
					"Die Organisation konnte nicht gefunden werden. Bitte wenden Sie sich an den Support.",
				type: "error",
			});
			return;
		}

		// Take first 8 characters of the uuid
		const uuid = uuidv4().substring(0, 8);
		const { error } = await supabase
			.from(SupabaseTableEnum.ALLOWED_SIGNUPS_LAB)
			.insert({
				email: `support-${uuid}@dentdesk.ch`,
				role: RolesEnum.OWNER,
				organization_id: organizationId,
			});

		if (error) {
			showNotification({
				message:
					"Beim Erstellen des Mitarbeiters ist ein Fehler aufgetreten. Bitte wenden Sie sich an den Support.",
				type: "error",
			});
			Logger.error(error, {}, "submitInvite");
			return;
		}

		const organization = useCentralStore.getState().organization;
		sendTeamInviteEmail({
			recipientEmail: "julius@dentdesk.ch",
			labName: `SUPPORT Account für ${organization?.name} unter support-${uuid}@dentdesk.ch`,
		});
		showNotification({
			message: "Supportaccount erfolgreich erstellt.",
			type: "success",
		});

		getMembers();
		setSupportDialogOpen(false);
	};

	const profileRole = useCentralStore((state) => state.role);

	const [role, setRole] = useState(RolesEnum.MEMBER);

	const handleInviteUser = () => {
		if (
			subscription &&
			subscription.status &&
			subscription.status !== "trialing" &&
			subscription.status !== "canceled" &&
			(subscription.quantity ?? 0) <= activeUsers.length
		) {
			showNotification({
				message:
					"Sie haben ihr Nutzerlimit erreicht. Möchten Sie Ihr Abonnement anpassen?",
				type: "error",
				action: {
					label: "Abonnement anpassen",
					onClick: () => {
						navigate("/abonnement");
					},
				},
			});
			return;
		}
		openDialog();
	};

	const changeRole = (value: string) => {
		setRole(parseInt(value));
	};

	const changeNoProfile = (checked: boolean) => {
		setIsProfile(checked);
	};

	const handleColorChange = async (userId: string, color: string | null) => {
		const currentColors =
			(organization?.profile_colors as Record<string, string>) || {};
		const updatedColors = { ...currentColors };

		if (color === null) {
			delete updatedColors[userId];
		} else {
			updatedColors[userId] = color;
		}

		await updateProfileColors(updatedColors);
	};

	const handleEditUser = (user: any) => {
		setSelectedUser(user);
		setEditDialogOpen(true);
	};

	const handleCreateProfileForEmptyProfile = (row: any) => {
		const fullName = `${row.first_name ?? ""} ${
			row.last_name ?? ""
		}`.trim();
		setConvertingFromEmptyProfileId(row.id);
		setConvertingFromLabel(
			fullName || row.code || "Mitarbeiter ohne Profil"
		);
		setIsProfile(true);
		setRole(RolesEnum.MEMBER);
		setEmail("");
		setValidationError(null);
		setDialogOpen(true);
	};

	const closeEditDialog = () => {
		setEditDialogOpen(false);
		setSelectedUser(null);
	};

	const subscriptionMessage = () => {
		if (subscription && subscription.status) {
			if (
				subscription.status === "trialing" &&
				subscription.subscription_id === null
			) {
				return (
					<div className="flex items-center text-sm text-green-600 font-medium gap-2 bg-green-50 py-2 px-3 rounded-md border border-green-200">
						<span>
							Sie befinden sich in der Testphase, Sie können ihr
							Abonnement jederzeit starten.
						</span>
						<Button
							variant="outline"
							size="sm"
							className="h-8 ml-2"
							onClick={() => navigate("/abonnement")}
						>
							Abonnement erstellen
						</Button>
					</div>
				);
			} else if (
				subscription.status === "trialing" &&
				subscription.subscription_id !== null
			) {
				return (
					<div className="flex items-center text-sm font-medium gap-2 bg-blue-50 py-2 px-3 rounded-md border border-blue-100">
						Sie befinden sich in der Testphase eines aktiven
						Abonnements.
					</div>
				);
			} else if (subscription?.quantity === activeUsers.length) {
				return (
					<div className="flex items-center text-sm text-amber-700 gap-2 bg-amber-50 py-2 px-3 rounded-md border border-amber-100">
						Aktuell werden {activeUsers.length} von{" "}
						{subscription.quantity ?? 0} möglichen Nutzer verwendet.
					</div>
				);
			} else if ((subscription?.quantity ?? 0) > activeUsers.length) {
				return (
					<div className="flex items-center text-sm text-blue-600 gap-2 bg-blue-50 py-2 px-3 rounded-md border border-blue-100">
						<span className="font-medium">
							Sie nutzen aktuell nur {activeUsers.length} von{" "}
							{subscription?.quantity ?? 0} verfügbaren Nutzern.
						</span>
						<Button
							variant="outline"
							size="sm"
							className="h-8 ml-2"
							onClick={() => navigate("/abonnement")}
						>
							Abonnement anpassen
						</Button>
					</div>
				);
			} else if ((subscription?.quantity ?? 0) < activeUsers.length) {
				return (
					<div className="flex items-center text-sm text-red-600 gap-2 bg-red-50 py-2 px-3 rounded-md border border-red-200">
						<span className="font-medium">
							Ihr Abonnement ist nicht ausreichend für die Anzahl
							der Nutzer.
						</span>
						<Button
							variant="outline"
							size="sm"
							className="h-8 ml-2"
							onClick={() => navigate("/abonnement")}
						>
							Abonnement anpassen
						</Button>
					</div>
				);
			} else {
				Logger.error(subscription, {}, "subscription message error");
				return (
					<div className="flex items-center text-sm text-gray-600 gap-2 bg-gray-50 py-2 px-3 rounded-md border border-gray-200">
						Es liegt ein Fehler vor. Bitte wenden Sie sich an den
						Support.
					</div>
				);
			}
		} else {
			return (
				<div className="flex items-center text-sm text-red-600 font-medium gap-2 bg-red-50 py-2 px-3 rounded-md border border-red-200">
					<span>Sie haben kein Abonnement.</span>
					<Button
						variant="outline"
						size="sm"
						className="h-8 ml-2"
						onClick={() => navigate("/abonnement")}
					>
						Abonnement erstellen
					</Button>
				</div>
			);
		}
	};

	if (profileRole !== RolesEnum.OWNER && profileRole !== RolesEnum.ADMIN) {
		return (
			<div className="flex items-center justify-center h-full">
				<h1 className="text-3xl font-bold text-gray-800">
					Sie haben keine Berechtigung auf diese Seite zuzugreifen.
				</h1>
			</div>
		);
	}

	return (
		<div className="h-full w-full flex flex-col space-y-4 p-6">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					<h1 className="text-2xl font-semibold text-gray-900">
						Mitarbeiter
					</h1>
					<Button
						onClick={handleInviteUser}
						className="bg-primary hover:bg-primary/90"
						size="sm"
					>
						<PlusCircle className="mr-2 h-4 w-4" />
						Mitarbeiter hinzufügen
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => setCourierDialogOpen(true)}
						data-testid="courier-invite-button"
					>
						<PlusCircle className="mr-2 h-4 w-4" />
						Kurier einladen
					</Button>

					<div className="flex items-center space-x-2 ml-4">
						<Checkbox
							id="showOnlyActiveUsers"
							checked={showOnlyActiveUsers}
							onCheckedChange={(checked) =>
								setShowOnlyActiveUsers(checked as boolean)
							}
						/>
						<Label
							htmlFor="showOnlyActiveUsers"
							className="text-sm font-medium cursor-pointer"
						>
							Nur aktive Nutzer anzeigen
						</Label>
					</div>

					<Button
						variant="outline"
						size="sm"
						className="ml-2"
						onClick={() => getMembers()}
					>
						<RefreshCw className="h-3.5 w-3.5 mr-2" />
						Aktualisieren
					</Button>
				</div>

				<Button
					variant="outline"
					size="sm"
					onClick={() => setSupportDialogOpen(true)}
				>
					Supportaccount hinzufügen
				</Button>
			</div>

			{subscriptionMessage()}

			<div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 overflow-hidden">
				<DataGrid
					localeText={
						deDE.components.MuiDataGrid.defaultProps.localeText
					}
					rows={members.filter((member) =>
						showOnlyActiveUsers ? !member.deactivated_at : true
					)}
					columns={columns}
					columnVisibilityModel={{
						location: locations.length > 1,
					}}
					sx={{
						height: "100%",
						width: "100%",
						border: 0,
						"& .MuiDataGrid-footerContainer": {
							display: "none",
						},
						"& .MuiDataGrid-cell[data-field='actions']:focus": {
							outline: "none",
						},
						"& .MuiDataGrid-cell[data-field='actions']:focus-within":
							{
								outline: "none",
							},
						"& .MuiDataGrid-columnHeaders": {
							backgroundColor: "#f9fafb",
							borderBottom: "1px solid #e5e7eb",
						},
						"& .MuiDataGrid-row:hover": {
							backgroundColor: "#f9fafb",
						},
					}}
				/>
			</div>

			<SmallDialog
				dialogTitle={
					convertingFromEmptyProfileId
						? "Profil erstellen"
						: "Mitarbeiter einladen"
				}
				dialogText={
					convertingFromEmptyProfileId
						? `Einladung für ${convertingFromLabel} versenden. Nach der Anmeldung werden alle bisherigen Zuweisungen (z.B. an Auftragspositionen) automatisch auf das neue Profil übertragen.`
						: "Der Mitarbeiter kann sofort zugewiesen werden (z.B. an Auftragspositionen). Nach der Anmeldung werden alle Zuweisungen automatisch auf das neue Profil übertragen."
				}
				open={dialogOpen}
				onCancel={closeDialog}
				onSave={submitInvite}
				dialogActions={["cancel", "save"]}
				dialogActionsText={{ cancel: "Abbrechen", save: "Einladen" }}
			>
				<div className="flex flex-col gap-4 p-1">
					{!convertingFromEmptyProfileId && (
						<>
							<div className="flex items-center space-x-2">
								<Checkbox
									id="hasProfile"
									checked={isProfile}
									onCheckedChange={(checked) =>
										changeNoProfile(checked as boolean)
									}
								/>
								<Label
									htmlFor="hasProfile"
									className="text-sm font-medium"
								>
									Mitarbeiter soll eigenes Profil erstellen
								</Label>
							</div>

							<Separator className="my-2" />

							<CustomTextField
								label="Vorname"
								value={firstName}
								onChange={(event) =>
									setFirstName(event.target.value)
								}
								validationError={null}
							/>
							<CustomTextField
								label="Nachname"
								value={lastName}
								onChange={(event) =>
									setLastName(event.target.value)
								}
								validationError={null}
							/>
							<CustomTextField
								label="Code"
								value={code}
								onChange={(event) =>
									setCode(event.target.value)
								}
								validationError={null}
							/>
						</>
					)}

					{isProfile && (
						<>
							<CustomTextField
								label="E-Mail"
								value={email}
								onChange={handleChange}
								validationError={validationError}
							/>
							<FormControl className="mt-2">
								<FormLabel className="text-sm font-medium mb-2">
									Rolle
								</FormLabel>
								<RadioGroup
									value={role.toString()}
									onValueChange={changeRole}
									className="flex flex-col space-y-2"
								>
									<div className="flex items-center space-x-2">
										<RadioGroupItem
											value={RolesEnum.MEMBER.toString()}
											id="member"
										/>
										<Label htmlFor="member">
											Mitarbeiter
										</Label>
									</div>
									<div className="flex items-center space-x-2">
										<RadioGroupItem
											value={RolesEnum.ADMIN.toString()}
											id="admin"
										/>
										<Label htmlFor="admin">
											Administrator
										</Label>
									</div>
								</RadioGroup>
							</FormControl>
						</>
					)}
				</div>
			</SmallDialog>

			<SmallDialog
				dialogTitle="Supportaccount hinzufügen"
				dialogText="Sie können dem Support von dentDESK einen Account erstellen."
				open={supportDialogOpen}
				onCancel={() => setSupportDialogOpen(false)}
				onSave={handleSupportAccountCreation}
				dialogActions={["cancel", "save"]}
				dialogActionsText={{ cancel: "Abbrechen", save: "Hinzufügen" }}
			>
				<div className="py-2">
					<p className="text-sm text-gray-500">
						Bei Aktivierung wird ein Support-Konto erstellt, das dem
						dentDESK-Team Zugriff auf Ihre Organisation ermöglicht,
						um Sie besser unterstützen zu können.
					</p>
				</div>
			</SmallDialog>

			<Dialog
				open={editDialogOpen}
				onOpenChange={(open) => !open && closeEditDialog()}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Mitarbeiter bearbeiten</DialogTitle>
						<DialogDescription>
							{selectedUser?.role === "Mitarbeiter ohne Profil"
								? "Bearbeiten Sie die Daten des Mitarbeiters ohne Profil."
								: "Bearbeiten Sie die Daten des Mitarbeiters."}
						</DialogDescription>
					</DialogHeader>
					{selectedUser &&
					selectedUser.role === "Mitarbeiter ohne Profil" ? (
						<EmptyProfileUpdateForm
							emptyProfileId={selectedUser.id}
							onClose={closeEditDialog}
						/>
					) : selectedUser && selectedUser.id ? (
						<UserUpdateForm
							userId={selectedUser.id}
							onClose={closeEditDialog}
						/>
					) : null}
				</DialogContent>
			</Dialog>

			<Dialog
				open={courierDialogOpen}
				onOpenChange={setCourierDialogOpen}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Kurier einladen</DialogTitle>
						<DialogDescription>
							Geben Sie die Daten des Kuriers ein. Kuriere
							erhalten ein eigenes, vereinfachtes Dashboard.
						</DialogDescription>
					</DialogHeader>
					<div className="flex flex-col gap-4 pt-2">
						<CustomTextField
							label="E-Mail"
							value={courierEmail}
							onChange={(e) => {
								setCourierEmail(e.target.value);
								setCourierValidationError(
									_validateField("email", e.target.value)
								);
							}}
							validationError={courierValidationError}
						/>
						<CustomTextField
							label="Vorname"
							value={courierFirstName}
							onChange={(e) =>
								setCourierFirstName(e.target.value)
							}
							validationError={null}
						/>
						<CustomTextField
							label="Nachname"
							value={courierLastName}
							onChange={(e) => setCourierLastName(e.target.value)}
							validationError={null}
						/>
						<div className="flex justify-end gap-2 pt-2">
							<Button
								variant="outline"
								onClick={() => setCourierDialogOpen(false)}
							>
								Abbrechen
							</Button>
							<Button onClick={submitCourierInvite}>
								Einladen
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
};
